import { useOutletContext } from "react-router";
import { useLocale, useTranslations } from "@acto/web-common/locales";
import { useComparisonModeContext, useCurrencyContext } from "@acto/taskpane/core/contexts/TenantSettingsContext";
import { Kpi } from "@acto/taskpane/core/domain";
import { Kpi as GqlKpi } from "@acto/gql/graphql";
import type { KpiPairTrend } from "@acto/taskpane/field-sales-2/components/primitives/KpiPairCard";
import type { CustomerOutletContext } from "@acto/taskpane/field-sales-2/CustomerDetailsLayout";
import { formatCompactCurrency, formatCompactPercent } from "@acto/taskpane/field-sales-2/utils/format";
import { buildKpiTrend } from "./buildKpiTrend";
import { toKpiPairTrend } from "./toKpiPairTrend";
import { useKpiSummaries } from "./useKpiTrend";

const MARGIN_KPIS = [GqlKpi.Profit, GqlKpi.Margin];

/**
 * Contribution margin (bar KPI) and margin ratio (line KPI) for the customer outlet's date range, compared in
 * the tenant's comparison mode.
 */
export function useCustomerMarginTrend(clientId: string | undefined, productGroupId?: string): KpiPairTrend {
  const { dateRange, isCustomRange } = useOutletContext<CustomerOutletContext>();
  const { locale } = useLocale();
  const currency = useCurrencyContext();
  const comparisonMode = useComparisonModeContext();
  const tKpi = useTranslations("kpi");
  const tAnalysis = useTranslations("fieldSalesTwo.analysis");

  const { values, fetching, stale, granularity } = useKpiSummaries(
    clientId,
    comparisonMode,
    MARGIN_KPIS,
    dateRange,
    productGroupId,
  );

  const build = (selectedKpi: Kpi) =>
    buildKpiTrend({
      values,
      fetching,
      stale,
      selectedKpi,
      marginAsRatio: true,
      comparisonMode,
      dateRange,
      isCustomRange,
      granularity,
      locale,
      currency,
      tKpi,
    });

  return toKpiPairTrend(build(Kpi.PROFIT), build(Kpi.MARGIN), {
    barKpi: tAnalysis("contributionMargin"),
    lineKpi: tKpi("marginTab"),
    currentPeriodLabel: tAnalysis("currentPeriod"),
    formatBarAxis: (value) => formatCompactCurrency(value, currency, locale),
    formatLineAxis: (value) => formatCompactPercent(value, locale),
    // Margin is a 0..1 ratio, so the axis steps in whole percentage points.
    lineTickUnit: 0.01,
  });
}
